<script setup lang="ts">
import { onBeforeUnmount, onMounted, onUnmounted, PropType, ref, VNode } from "vue";
import { DesignVersion } from "@/models/types";
import { MenuEntry } from "@/models/menu-entry";
import BcMenu from "./bc-menu.vue";

defineOptions({ name: "BcContextMenu" });
const emit = defineEmits<{
  (e: "contextmenu"): void;
  (e: "custom-event", msg: string): void;
}>();

const menuRef = ref<InstanceType<typeof BcMenu>>();
const items = ref([]);
const x = ref(0);
const y = ref(0);
const isVisible = ref(false);
let container = document.body;

const props = defineProps({
  design: {
    type: String as PropType<DesignVersion>,
    default: "7new",
    validator: (value: any) => DesignVersion.isDesignVersion(value),
  },
  subItemIcon: { type: Object as PropType<VNode> },
  separateAll: { type: Boolean, default: false },
  getItems: {
    type: Function,
    default: (_event: MouseEvent): Array<MenuEntry> => [],
  },
  containerId: { type: String },
});

onMounted(() => {
  container = document.body;
  if (!!props.containerId && props.containerId !== "") {
    container = document.getElementById(props.containerId ?? "") ?? container;
  }

  if (menuRef.value) container.appendChild(menuRef.value.$el);

  container.addEventListener("contextmenu", onContainerContextMenu, { capture: true });
  container.addEventListener("keydown", onContainerKeypress, { capture: true });
  document.addEventListener("click", hideContextMenuOnEvent, { capture: true });
  document.addEventListener("scroll", hideContextMenuOnEvent, { capture: true });
  window.addEventListener("resize", hideContextMenu, { capture: true });
});

onBeforeUnmount(() => {
  if (menuRef.value) container.removeChild(menuRef.value.$el);
});

onUnmounted(() => {
  container.removeEventListener("contextmenu", onContainerContextMenu, { capture: true });
  container.addEventListener("keydown", onContainerKeypress, { capture: true });
  document.removeEventListener("click", hideContextMenuOnEvent, { capture: true });
  document.removeEventListener("scroll", hideContextMenuOnEvent, { capture: true });
  window.removeEventListener("resize", hideContextMenu, { capture: true });
});

function hideContextMenu() {
  isVisible.value = false;
}

function hideContextMenuOnEvent(event: Event) {
  let contextMenuDiv = menuRef.value?.$el;
  if (
    event.target !== contextMenuDiv &&
    !contextMenuDiv?.contains(event.target as Node)
  ) {
    isVisible.value = false;
  }
}

function onContainerKeypress(event: KeyboardEvent) {
  if (event.key === "Escape" || event.key === "Esc") {
    hideContextMenuOnEvent(event);
  }
}

function onContainerContextMenu(event: MouseEvent) {
  event.preventDefault();

  if (!menuRef.value || menuRef.value.$el?.contains(event.target as Node)) {
    return;
  }

  items.value = props.getItems(event);
  x.value = event.x;
  y.value = event.y;
  isVisible.value = !!items.value && items.value.length > 0;
  emit("contextmenu");
}

defineExpose({
  hideContextMenu,
});
</script>

<template>
  <bc-menu
    ref="menuRef"
    :design="design"
    :separateAll="separateAll"
    :subItemIcon="subItemIcon"
    :isVisible="isVisible"
    :x="x"
    :y="y"
    :items="items"
  />
</template>
